import { FiscalEnvironmentEnum } from 'src/app/domain/registration/enums/fiscal-environment.enum';
import { RegimeTypeEnum } from 'src/app/domain/registration/enums/regime-type.enum';

export const MATRIZ_BRANCH_TITLE = 'Matriz';

export interface BranchFiscalParameter {
  regime: string;
  mainActivity: string;
  fiscalEnvironment: string;
  csc: string;
  cscId: string;
}

export interface CustomerMainAddress {
  cep?: string;
  address?: string;
  addressNumber?: string | number;
  addressComplement?: string;
  neighborhood?: string;
  state?: string;
  city?: string;
  document?: string;
  stateRegistration?: string;
}

export interface CustomerBranch {
  localKey: string;
  id?: string | null;
  branchTitle: string;
  branchDocument: string;
  branchStateRegistration: string;
  branchCep: string;
  branchAddress: string;
  branchAddressNumber: string;
  branchAddressComplement: string;
  branchNeighborhood: string;
  branchState: string;
  branchCity: string;
  branchCountry: string;
  branchObservation: string;
  branchIsMain: boolean;
  branchActive: boolean;
  fiscalParameter: BranchFiscalParameter;
}

export function defaultFiscalParameter(
  source?: Partial<BranchFiscalParameter> | null
): BranchFiscalParameter {
  return {
    regime: text(source?.regime) || RegimeTypeEnum.SIMPLE,
    mainActivity: text(source?.mainActivity),
    fiscalEnvironment: text(source?.fiscalEnvironment) || FiscalEnvironmentEnum.HOMOLOCATION,
    csc: text(source?.csc),
    cscId: text(source?.cscId),
  };
}

export function isMatrizBranch(branch?: Partial<CustomerBranch> | null): boolean {
  if (!branch) return false;
  if (branch.branchIsMain) return true;
  return String(branch.branchTitle || '').trim().toLowerCase() === MATRIZ_BRANCH_TITLE.toLowerCase();
}

export function branchLocalKey(source: any, index: number): string {
  if (source?.localKey) return String(source.localKey);
  if (source?.id) return `id:${source.id}`;
  return `branch-${index}`;
}

function text(value: unknown): string {
  return value == null ? '' : String(value);
}

export function toCustomerBranch(
  source: any,
  index: number,
  isMain: boolean
): CustomerBranch {
  return {
    localKey: branchLocalKey(source, index),
    id: source?.id ?? null,
    branchTitle: isMain ? MATRIZ_BRANCH_TITLE : text(source?.branchTitle || source?.title),
    branchDocument: text(source?.branchDocument || source?.document),
    branchStateRegistration: text(source?.branchStateRegistration || source?.stateRegistration),
    branchCep: text(source?.branchCep || source?.cep),
    branchAddress: text(source?.branchAddress || source?.address),
    branchAddressNumber: text(source?.branchAddressNumber || source?.addressNumber || source?.number),
    branchAddressComplement: text(source?.branchAddressComplement || source?.addressComplement),
    branchNeighborhood: text(source?.branchNeighborhood || source?.neighborhood),
    branchState: text(source?.branchState || source?.state),
    branchCity: text(source?.branchCity || source?.city),
    branchCountry: text(source?.branchCountry || source?.country || 'BR') || 'BR',
    branchObservation: text(source?.branchObservation || source?.observation),
    branchIsMain: isMain,
    branchActive: source?.branchActive !== false && source?.active !== false,
    fiscalParameter: defaultFiscalParameter(source?.fiscalParameter),
  };
}

export function createMatrizBranch(
  source: any,
  main: CustomerMainAddress
): CustomerBranch {
  const matriz = toCustomerBranch(source, 0, true);
  return {
    ...matriz,
    branchTitle: MATRIZ_BRANCH_TITLE,
    branchIsMain: true,
    branchActive: true,
    branchDocument: matriz.branchDocument || text(main.document),
    branchStateRegistration: matriz.branchStateRegistration || text(main.stateRegistration),
    branchCep: text(main.cep || matriz.branchCep),
    branchAddress: text(main.address || matriz.branchAddress),
    branchAddressNumber: text(main.addressNumber || matriz.branchAddressNumber),
    branchAddressComplement: text(main.addressComplement || matriz.branchAddressComplement),
    branchNeighborhood: text(main.neighborhood || matriz.branchNeighborhood),
    branchState: text(main.state || matriz.branchState),
    branchCity: text(main.city || matriz.branchCity),
  };
}

export function normalizeBranches(
  loaded: any[] | null | undefined,
  main: CustomerMainAddress
): CustomerBranch[] {
  const list = Array.isArray(loaded) ? loaded : [];
  const matrizSource = list.find((branch) => isMatrizBranch(branch)) || null;
  const others = list.filter((branch) => branch !== matrizSource);
  return [
    createMatrizBranch(matrizSource, main),
    ...others.map((branch, index) => toCustomerBranch(branch, index + 1, false)),
  ];
}

export function syncMatrizAddress(
  branches: CustomerBranch[],
  main: CustomerMainAddress
): CustomerBranch[] {
  if (!branches.length) return normalizeBranches([], main);
  return branches.map((branch, index) => {
    if (index !== 0 && !branch.branchIsMain) return branch;
    return {
      ...branch,
      branchTitle: MATRIZ_BRANCH_TITLE,
      branchIsMain: true,
      branchCep: text(main.cep),
      branchAddress: text(main.address),
      branchAddressNumber: text(main.addressNumber),
      branchAddressComplement: text(main.addressComplement),
      branchNeighborhood: text(main.neighborhood),
      branchState: text(main.state),
      branchCity: text(main.city),
      branchDocument: branch.branchDocument || text(main.document),
      branchStateRegistration: branch.branchStateRegistration || text(main.stateRegistration),
    };
  });
}

export function filterBranches(branches: CustomerBranch[], term: string): CustomerBranch[] {
  const query = (term || '').trim().toLowerCase();
  if (!query) return branches;
  return branches.filter((branch) =>
    [
      branch.branchTitle,
      branch.branchDocument,
      branch.branchCity,
      branch.branchState,
      branch.branchAddress,
      branch.branchCep,
    ]
      .join(' ')
      .toLowerCase()
      .includes(query)
  );
}

export function upsertBranch(
  branches: CustomerBranch[],
  branch: CustomerBranch,
  index: number
): CustomerBranch[] {
  const next: CustomerBranch = branch.branchIsMain || isMatrizBranch(branch)
    ? { ...branch, branchTitle: MATRIZ_BRANCH_TITLE, branchIsMain: true, branchActive: true }
    : { ...branch, branchIsMain: false };
  next.fiscalParameter = defaultFiscalParameter(
    branch.fiscalParameter || branches[index]?.fiscalParameter
  );
  if (index >= 0 && index < branches.length) {
    return branches.map((item, itemIndex) =>
      itemIndex === index
        ? { ...item, ...next, localKey: item.localKey, id: item.id ?? next.id }
        : item
    );
  }
  return [...branches, { ...next, localKey: next.localKey || `branch-${branches.length}` }];
}

export function removeBranch(branches: CustomerBranch[], index: number): CustomerBranch[] {
  const target = branches[index];
  if (!target || index === 0 || isMatrizBranch(target)) return branches;
  return branches.filter((_, itemIndex) => itemIndex !== index);
}

export function branchStatusLabel(branch: CustomerBranch): string {
  return branch.branchActive === false ? 'INATIVA' : 'ATIVA';
}
